import { useCallback } from "react";
import { useAuth } from "../../../context/AuthContext";
import useCurrentUser from "../../user-management/hooks/useCurrentUser";
import { canAccessFeature as checkFeatureAccess } from "../utils/featureAccess";
import type { FeatureKey } from "../types";

export interface UseFeatureAccessReturn {
  /** True if the current user has access to the given feature (plan or Internal/SuperAdmin). */
  canAccessFeature: (featureKey: FeatureKey) => boolean;
  /** Current user's subscription product name (e.g. "Sagelyn Pro"), if loaded. */
  subscriptionProductName: string | undefined;
  /** Whether current user profile is still loading (plan may be unknown). */
  loading: boolean;
}

/**
 * Hook to gate features by self-service plan and user group.
 * Uses auth userGroups and current user's subscription_product_name from the user
 * service (the backend stores product name, not a tier code).
 */
export function useFeatureAccess(): UseFeatureAccessReturn {
  const { userGroups } = useAuth();
  const { user, loading } = useCurrentUser();
  const subscriptionProductName = user?.subscription_product_name;
  const tier = user?.tier;

  const canAccessFeature = useCallback(
    (featureKey: FeatureKey) =>
      checkFeatureAccess(featureKey, userGroups, subscriptionProductName, tier),
    [userGroups, subscriptionProductName, tier]
  );

  return { canAccessFeature, subscriptionProductName, loading };
}

export default useFeatureAccess;
